import {ReactNode, useMemo, useState} from 'react'
import {ComboboxItem, ComboboxProvider, SelectProvider} from '@ariakit/react'
import {useTranslation} from '@/translations/i18n.tsx'
import {XCloseIcon} from '@/components/ui/icon/Icon'
import {Label} from '@/components/ui/label/Label'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'
import {
    SelectComboboxInput,
    SelectComboboxList,
    SelectComboboxPopover,
    SelectTrigger
} from '@/components/ui/select-atoms/SelectAtoms'
import {
    MultiSelectAccent,
    StyledChip,
    StyledChipOverflow,
    StyledChipsRow,
    StyledLoadMoreRow,
    StyledMultiSelectItem,
    StyledMultiSelectWrapper,
    StyledOptionLabel,
    StyledSelectAllRow
} from '@/features/mailer/components/mailer-multi-select/style'

export interface MailerMultiSelectOption {
    value: string
    label: string
    /** Muted prefix rendered before the label in the options list (e.g. diagnosis code). */
    prefix?: string
}

interface MailerMultiSelectProps {
    label: ReactNode
    options: MailerMultiSelectOption[]
    value: string[]
    onChange: (value: string[]) => void
    placeholder: string
    accent?: MultiSelectAccent
    disabled?: boolean
    /**
     * Server-search mode for large domains (e.g. 11k+ diagnoses): the parent owns
     * the query and receives the typed text; client-side filtering is skipped and
     * the "Seleziona tutte" row is hidden (the whole domain is unknown).
     */
    onSearch?: (search: string) => void
    isLoading?: boolean
    isFetchingNextPage?: boolean
    hasNextPage?: boolean
    onLoadMore?: () => void
    /** Chip labels for selected values not present in `options` (server-search mode). */
    selectedLabelByValue?: Record<string, string>
}

/**
 * Reusable multi-select per design §5.4 / spec §6, built on the design-system
 * select atoms (Ariakit): portal popover (never clipped by overflow containers,
 * no scroll hijacking), searchable list, "Seleziona tutte / Deseleziona tutte"
 * acting on the WHOLE domain, removable chips (max 3 + "+N") hidden when all
 * options are selected, and a "Tutte (N)" trigger state.
 */
export const MailerMultiSelect = ({
    label,
    options,
    value,
    onChange,
    placeholder,
    accent = 'primary',
    disabled = false,
    onSearch,
    isLoading = false,
    isFetchingNextPage = false,
    hasNextPage = false,
    onLoadMore,
    selectedLabelByValue
}: MailerMultiSelectProps) => {
    const {t} = useTranslation()
    const [search, setSearch] = useState('')
    const isServerSearch = !!onSearch

    const filteredOptions = useMemo(() => {
        if (isServerSearch) return options
        const term = search.trim().toLowerCase()
        if (!term) return options
        return options.filter(option => `${option.prefix ?? ''} ${option.label}`.toLowerCase().includes(term))
    }, [isServerSearch, options, search])

    const allSelected = !isServerSearch && options.length > 0 && value.length === options.length

    const labelByValue = useMemo(() => {
        const map: Record<string, string> = {...selectedLabelByValue}
        for (const option of options) map[option.value] = option.label
        return map
    }, [options, selectedLabelByValue])

    const toggleAll = () => onChange(allSelected ? [] : options.map(option => option.value))
    const removeValue = (optionValue: string) => onChange(value.filter(item => item !== optionValue))

    const triggerText = (selected: string | string[]) => {
        const selectedValues = Array.isArray(selected) ? selected : [selected]
        if (allSelected) return t('mailer:multiselect_all_selected', {count: selectedValues.length})
        if (selectedValues.length === 1) return labelByValue[selectedValues[0]] ?? selectedValues[0]
        return t('mailer:multiselect_n_selected', {count: selectedValues.length})
    }

    return (
        <StyledMultiSelectWrapper>
            <Label>{label}</Label>
            <ComboboxProvider
                value={search}
                setValue={nextSearch => {
                    setSearch(nextSearch)
                    onSearch?.(nextSearch)
                }}
                resetValueOnHide
            >
                <SelectProvider value={value} setValue={onChange} defaultValue={[]}>
                    <SelectTrigger fullWidth disabled={disabled} placeholder={placeholder} displayValue={triggerText} />
                    <SelectComboboxPopover sameWidth portal>
                        <SelectComboboxInput placeholder={t('mailer:multiselect_search_placeholder')} />
                        <SelectComboboxList>
                            {!isServerSearch && options.length > 0 && (
                                <StyledSelectAllRow type="button" onClick={toggleAll}>
                                    {allSelected
                                        ? t('mailer:multiselect_deselect_all')
                                        : t('mailer:multiselect_select_all')}
                                </StyledSelectAllRow>
                            )}
                            {isLoading ? (
                                Array.from({length: 6}, (_, index) => <Skeleton key={index} height={32} />)
                            ) : filteredOptions.length === 0 ? (
                                <InputHelpText helpText={t('mailer:multiselect_no_results')} />
                            ) : (
                                filteredOptions.map(option => (
                                    <StyledMultiSelectItem
                                        key={option.value}
                                        value={option.value}
                                        render={<ComboboxItem />}
                                        selectItemContentClassName="content"
                                    >
                                        <StyledOptionLabel>
                                            {option.prefix && <span className="prefix">{option.prefix}</span>}
                                            {option.label}
                                        </StyledOptionLabel>
                                        <Checkbox
                                            id={`mailer-multi-select-${option.value}`}
                                            checked={value.includes(option.value)}
                                            onChange={() => undefined}
                                        />
                                    </StyledMultiSelectItem>
                                ))
                            )}
                            {!isLoading && hasNextPage && (
                                <StyledLoadMoreRow
                                    type="button"
                                    disabled={isFetchingNextPage}
                                    onClick={() => onLoadMore?.()}
                                >
                                    {isFetchingNextPage ? <Spinner size={14} /> : t('mailer:multiselect_load_more')}
                                </StyledLoadMoreRow>
                            )}
                        </SelectComboboxList>
                    </SelectComboboxPopover>
                </SelectProvider>
            </ComboboxProvider>

            {value.length > 0 && !allSelected && (
                <StyledChipsRow>
                    {value.slice(0, 3).map(selectedValue => (
                        <StyledChip key={selectedValue} $accent={accent}>
                            {labelByValue[selectedValue] ?? selectedValue}
                            <button
                                type="button"
                                aria-label={`${t('mailer:close')} ${labelByValue[selectedValue] ?? selectedValue}`}
                                onClick={() => removeValue(selectedValue)}
                            >
                                <XCloseIcon />
                            </button>
                        </StyledChip>
                    ))}
                    {value.length > 3 && <StyledChipOverflow>+{value.length - 3}</StyledChipOverflow>}
                </StyledChipsRow>
            )}
        </StyledMultiSelectWrapper>
    )
}

MailerMultiSelect.displayName = 'MailerMultiSelect'
